import React from "react";
import { DropdownMenu, Input, MenuItem } from "react-ui";
import { Fit, Fixed } from "@skbkontur/react-stack-layout";
import { useDebounce } from "use-debounce";
import { PopupMenuCaptionProps } from "@skbkontur/react-ui/internal/PopupMenu";

import { isNotNullOrEmpty, isNotNullOrUndefined } from "../../../../../Common/TypingUtils";
import { emptyRef } from "../../TypingUtils";
import { ControlTooltipProps } from "../../Internals/Tooltip/ControlTooltipProps";
import { ControlValidationProps } from "../../Validations/ValidationProps";
import { PicklistDataItem } from "../../../Engine/Core/Picklists/PicklistTypes";
import { useAsyncEffect } from "../../../../../Common/UseAsyncEffect";
import { FocusManagementObserver } from "../../Services/FocusManagement/FocusManagementObserver";
import { useScrollTo } from "../../Internals/Contexts/ScrollToContext";
import { useValueEditMetrics } from "../../Internals/Metrics/MetricsProviderContext";
import { AbsoluteModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { isEnterKey } from "../../../Helpers/KeyboardUtils";
import { Text } from "../../ValueViewers/Text/Text";
import { useTestPageObject } from "../../Services/TestPageObjects/PageObjectExposure";
import { getSelectionFromEvent } from "../../../Helpers/GetSelectionFromEvent";
import { Tooltip } from "../../Internals/Tooltip/Tooltip";

import { splitTaxRebateValue, TaxRebateValueInfo } from "./TaxRebateValueSplit";
import { ComboboxMenu } from "./ComboboxMenu/ComboboxMenu";
import { TaxRebateTestPageObject } from "./TaxRebateTestPageObject";
import { fakeFocus, MenuItemContent, TaxRebateLayout } from "./TaxRebate.styles";
import { TaxRebateLaws, TaxRebateLawsProps } from "./TaxRebateLaws";
import {
    buildValue,
    lawValueRegexp,
    splitTaxRebateValueNoHasLaws,
    taxRebateItemFromDigestResponse,
    taxRebateItemMatchTo,
} from "./TaxRebateUtils";

export interface TaxRebateColumnNamesMap {
    code: string;
    name: string;
    lawReferenceRequired: undefined | string;
}

export interface TaxRebateItemLawPattern {
    law1Length: number;
    law2Length: number;
    law3Length: number;
    law1Delimiter: string;
    law2Delimiter: string;
    law3Delimiter: string;
    codeLength: number;
}

export interface TaxRebateLawPattern extends TaxRebateItemLawPattern {
    law1Name?: string;
    law2Name?: string;
    law3Name?: string;
    lawNote?: string;
}

export interface TaxRebateProps extends ControlTooltipProps, ControlValidationProps {
    bindingPath: AbsoluteModelPath;
    value: string | undefined;
    onChange: (nextValue: string) => void;
    columnNamesMap?: TaxRebateColumnNamesMap;
    codesWithLawReference?: string[];
    placeholder?: string;
    width?: number | string;
    onFetchAllTaxRebates: () => Promise<PicklistDataItem[]>;
    isReadOnly?: boolean;
    onClick?: () => void;
    isLink?: boolean;
    lawPattern: TaxRebateLawPattern;
    gId: string;
    "data-tid"?: string;
}

export interface TaxRebateItem {
    code: string;
    name: string;
    lawReferenceRequired: boolean;
}

export const TaxRebate = (props: TaxRebateProps) => {
    if (props.isReadOnly) {
        return (
            <Text
                value={props.value}
                bindingPath={props.bindingPath}
                data-tid={props["data-tid"]}
                onClick={props.onClick}
                isLink={props.isLink}
            />
        );
    }
    return <TaxRebateInteractive {...props} />;
};

const TaxRebateInteractive = (props: TaxRebateProps) => {
    const scrollTo = useScrollTo();
    const [trackStartEdit, trackFinishEdit] = useValueEditMetrics(props.bindingPath);
    const input = React.useRef<Input>(emptyRef);
    const law1Input = React.useRef<Input>(emptyRef);
    const menu = React.useRef<ComboboxMenu>(emptyRef);
    const dropdownMenuRef = React.useRef<DropdownMenu>(emptyRef);

    const [allTaxRebates, setAllTaxRebates] = React.useState<TaxRebateItem[]>([]);
    useAsyncEffect(async () => {
        if (isNotNullOrEmpty(props.gId)) {
            setAllTaxRebates(
                taxRebateItemFromDigestResponse(
                    await props.onFetchAllTaxRebates(),
                    props.columnNamesMap,
                    props.codesWithLawReference
                )
            );
        }
    }, []);

    const [dropdownMode, setDropdownMode] = React.useState<"none" | "code" | "law1" | "law2" | "law3">("none");
    const [focused, setFocused] = React.useState(false);
    const [focusMode, setFocusMode] = React.useState<"input" | "laws">();
    const [inputCursorPosition, setInputCursorPosition] = React.useState(0);
    const { law1Length, law2Length, law3Length, law1Delimiter, codeLength } = props.lawPattern;
    const law1DefaultValue = law1Length > 0 ? "0" : "";
    const law2DefaultValue = law2Length > 0 ? "0" : "";
    const law3DefaultValue = law3Length > 0 ? "0" : "";
    const splitValue = splitTaxRebateValue(props.value ?? "", props.lawPattern);
    const [valueToSearch] = useDebounce(splitValue.code.rawValue ?? "", 300);
    const viewMode = isNotNullOrEmpty(props.gId) ? "combobox" : "input";

    const updateMode = (targetSplitValue: TaxRebateValueInfo, position: number) => {
        setInputCursorPosition(position);
        if (targetSplitValue.code.start <= position && position <= targetSplitValue.code.end) {
            setDropdownMode("code");
        } else if (
            targetSplitValue.law1 &&
            targetSplitValue.law1.start <= position &&
            position <= targetSplitValue.law1.end
        ) {
            setDropdownMode("law1");
        } else if (
            targetSplitValue.law2 &&
            targetSplitValue.law2.start <= position &&
            position <= targetSplitValue.law2.end
        ) {
            setDropdownMode("law2");
        } else if (
            targetSplitValue.law3 &&
            targetSplitValue.law3.start <= position &&
            position <= targetSplitValue.law3.end
        ) {
            setDropdownMode("law3");
        } else {
            setDropdownMode("none");
        }
    };

    const handleKeyUp = (e: React.KeyboardEvent<HTMLElement>) => {
        updateMode(splitValue, getSelectionFromEvent(e.target).start);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === "Escape") {
            e.preventDefault();
            dropdownMenuRef.current?.close?.();
        }
        if (viewMode === "combobox") {
            handleСomboboxKeyDown(e);
        }
    };

    const handleСomboboxKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === "ArrowDown" || e.key === "Down") {
            e.preventDefault();
            menu.current?.down();
        }
        if (e.key === "ArrowUp" || e.key === "Up") {
            e.preventDefault();
            menu.current?.up();
        }
        if (dropdownMode === "code") {
            if (isEnterKey(e)) {
                e.preventDefault();
                menu.current?.enter(e);
            }
        }
    };

    const handleInputModeInputValueChange = () => {
        const item: TaxRebateItem = {
            code: splitValue?.code.value ?? "",
            name: "",
            lawReferenceRequired: true,
        };
        handleLawReferenceRequiredCode(item);
    };

    const handleSelectCode = (item: TaxRebateItem, e: React.SyntheticEvent) => {
        if (item.lawReferenceRequired) {
            handleLawReferenceRequiredCode(item);
            if (e.type === "click") {
                setTimeout(() => {
                    dropdownMenuRef.current?.open?.();

                    setTimeout(() => {
                        law1Input.current?.focus?.();
                    });
                });
            }
        } else {
            props.onChange(item.code);
            input.current?.setSelectionRange(item.code.length, item.code.length);
            input.current?.focus();
            updateMode(splitTaxRebateValue(item.code, props.lawPattern), item.code.length);
        }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        if (!focused) {
            setFocused(true);
            setFocusMode("input");
            trackStartEdit(props.value ?? "");
            updateMode(splitValue, getSelectionFromEvent(e.target).start);
        }
    };

    const handleInputValueChange = (nextInputValue: string) => {
        if (!lawValueRegexp.test(nextInputValue)) {
            input.current?.blink();
            return;
        }

        if (viewMode === "input" && splitTaxRebateValueNoHasLaws(splitValue) && codeLength > 0) {
            if (nextInputValue.length > codeLength) {
                nextInputValue = nextInputValue.slice(0, codeLength);
            }
            if (nextInputValue.length === codeLength) {
                handleInputModeInputValueChange();
            }
        }

        props.onChange(nextInputValue);
    };

    const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
        setFocused(true);
        setFocusMode("input");
        updateMode(splitValue, getSelectionFromEvent(e.target).start);
    };

    const handleBlur = () => {
        setFocused(false);
        const value = splitValue.code.value;
        const nextValue = buildValue(
            value ?? "",
            {
                law1: splitValue.law1?.rawValue ?? law1DefaultValue,
                law2: splitValue.law2?.rawValue ?? law2DefaultValue,
                law3: splitValue.law3?.rawValue ?? law3DefaultValue,
            },
            props.lawPattern
        );
        const item = allTaxRebates.find(x => x.code === value);
        if (viewMode === "combobox" && (item?.lawReferenceRequired ?? false)) {
            props.onChange(nextValue);
            trackFinishEdit(nextValue);
        } else if (viewMode === "input") {
            props.onChange(isNotNullOrEmpty(value) ? nextValue : "");
            trackFinishEdit(nextValue);
        } else {
            trackFinishEdit(props.value ?? "");
        }
    };

    const handleInputBlur = () => {
        setFocusMode("laws");
    };

    const handleRequestFocus = (): void => {
        const node = input.current?.getNode();
        if (isNotNullOrUndefined(node)) {
            scrollTo(node);
            input.current?.focus();
        }
    };

    const handleLawReferenceRequiredCode = (item: TaxRebateItem) => {
        const nextValue = buildValue(
            item.code,
            { law1: splitValue.law1?.value, law2: splitValue.law2?.value, law3: splitValue.law3?.value },
            props.lawPattern
        );
        props.onChange(nextValue);
        const position = item.code.length + law1Delimiter.length;
        input.current?.setSelectionRange(position, position);
        updateMode(splitTaxRebateValue(nextValue, props.lawPattern), position);
    };

    const taxRebateLawsProps: TaxRebateLawsProps = {
        value: props.value,
        lawPattern: props.lawPattern,
        focused: focused && focusMode === "input",
        onChange: props.onChange,
        law1Input: law1Input,
        parentInputCursorPosition: inputCursorPosition,
        onLawEnterKeyDown: handleBlur,
    };

    useTestPageObject(() => TaxRebateTestPageObject(props), [props.value, props.warning, props.error]);

    const { error: errorMessage, warning: warningMessage } = props;
    const tooltipMessage = errorMessage || warningMessage;
    const width = props.width ?? "100%";

    const DropdownInput = (popupMenuprops: PopupMenuCaptionProps) => {
        const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
            popupMenuprops.openMenu();
            handleFocus(e);
        };
        return (
            <Input
                data-tid={props["data-tid"] ?? "PrimaryInput"}
                width={width}
                onFocus={onFocus}
                placeholder={props.placeholder}
                css={focused && focusMode != "input" ? fakeFocus : undefined}
                value={props.value ?? ""}
                error={Boolean(errorMessage)}
                warning={Boolean(warningMessage)}
                onValueChange={handleInputValueChange}
                ref={input}
                onClick={handleInputClick}
                onBlur={handleInputBlur}
                onKeyUp={handleKeyUp}
                onKeyDown={handleKeyDown}
            />
        );
    };

    return (
        <TaxRebateLayout width={width}>
            <FocusManagementObserver onRequestFocus={handleRequestFocus}>
                <Tooltip
                    pos={props.tooltipPosition ?? "top left"}
                    trigger={tooltipMessage ? "hover&focus" : "closed"}
                    value={tooltipMessage}
                    width={props.tooltipWidth}>
                    <DropdownMenu
                        caption={DropdownInput}
                        width={width}
                        menuMaxHeight={300}
                        onOpen={() => {
                            if (dropdownMode === "code") {
                                setTimeout(() => {
                                    input?.current?.focus?.();
                                });
                            }

                            if (dropdownMode === "law1") {
                                setTimeout(() => {
                                    law1Input.current?.focus?.();
                                }, 100); // 100 ms for animation
                            }
                        }}
                        onClose={handleBlur}
                        ref={dropdownMenuRef}>
                        {dropdownMode === "code" &&
                            viewMode === "combobox" &&
                            allTaxRebates
                                .filter(x => taxRebateItemMatchTo(x, valueToSearch))
                                .map(x => (
                                    <MenuItem key={x.code} onClick={e => handleSelectCode(x, e)}>
                                        <MenuItemContent>
                                            <Fixed width={80}>{x.code}</Fixed>
                                            <Fit>{x.name}</Fit>
                                        </MenuItemContent>
                                    </MenuItem>
                                ))}
                        {(dropdownMode === "law1" || dropdownMode === "law2" || dropdownMode === "law3") && (
                            <TaxRebateLaws {...taxRebateLawsProps} />
                        )}
                    </DropdownMenu>
                </Tooltip>
            </FocusManagementObserver>
        </TaxRebateLayout>
    );
};
